import _ from 'lodash';
/** import third-party libraries in the section above, Sort Lines Ascending */

import { isArray, isString } from 'utils/validate';
/** import application imports in the section above, Sort Lines Ascending */

const geotargetsIncludeExcludeSchema = {
    city(value) {
        if (value === undefined) {
            return;
        }

        if (!isString().validate(value)) {
            return 'City is required';
        }
    },

    region(value) {
        if (value === undefined) {
            return;
        }

        if (!isString().validate(value)) {
            return 'Region is required';
        }
    },
};

const geoboxSchema = {
    E(value) {
        if (!_.isNumber(value)) {
            return 'E must be a number';
        }
    },
    N(value) {
        if (!_.isNumber(value)) {
            return 'N must be a number';
        }
    },
    S(value) {
        if (!_.isNumber(value)) {
            return 'S must be a number';
        }
    },
    W(value) {
        if (!_.isNumber(value)) {
            return 'W must be a number';
        }
    },
};

const geotargetsSchema = {
    country(value) {
        if (!isString().validate(value)) {
            return 'Country is required';
        }
    },

    exclude(value) {
        if (!_.isArray(value)) {
            return 'Exclude must be an array';
        }

        return geotargetsIncludeExcludeSchema;
    },

    include(value) {
        if (!_.isArray(value)) {
            return 'Include must be an array';
        }

        return geotargetsIncludeExcludeSchema;
    },
};

//-- validation rules --//
export function getDraftValidators_forPayload() {
    return {
        //==========
        // Basic
        //==========
        ad_preset_name(value) {
            if (!isString(1).validate(value)) {
                return 'Name is required';
            }
        },

        is_default_preset() {
            return;
        },

        geotargets(value) {
            if (value === undefined) {
                return;
            }

            if (!isArray(1).validate(value)) {
                return 'Geotargeting is required if selected. Either unselect it on the left list, or select at least one country.';
            }

            if (!_.every(value, _.isPlainObject)) {
                return 'Geotarget is an invalid type';
            }

            return geotargetsSchema;
        },

        target_age_groups(value) {
            if (value === undefined) {
                return;
            }

            if (!_.isArray(value)) {
                return 'Target Age Groups must be a list';
            }

            return ageGroupSchema;
        },

        target_carriers(value, source) {
            if (value === undefined) {
                return;
            }

            if (!_.isArray(value)) {
                return 'Carrier / ISP include must be a list';
            }

            // Compound include/exclude: when selected on the left list, require at least one
            // include or exclude entry (or the user must unselect the property).
            const exclude = Array.isArray(source.carriers_exclude) ? source.carriers_exclude : [];
            if (value.length === 0 && exclude.length === 0) {
                return 'Carrier / ISP is required if selected. Either unselect it on the left list, or include or exclude at least one item.';
            }
        },

        carriers_exclude(value) {
            if (value === undefined) {
                return;
            }

            if (!_.isArray(value)) {
                return 'Carrier / ISP exclude must be a list';
            }
        },

        target_genders(value) {
            if (value === undefined) {
                return;
            }

            if (!_.isArray(value)) {
                return 'Target Genders must be a list';
            }

            return gendersSchema;
        },

        max_cpm_rate_local(value) {
            if (value === undefined) {
                return;
            }

            if (!_.isNumber(value) || !(value > 0)) {
                return 'Total Bid Price is required if selected. Either unselect it on the left list, or enter a number greater than 0.';
            }
        },

        target_device_language(value, source) {
            if (value === undefined || value === null) {
                return;
            }

            // Must be a list — string/number payloads break apply/summary code that assumes arrays
            if (!_.isArray(value)) {
                return 'User Language must be a list';
            }

            // Compound include/exclude: when selected on the left list, require at least one
            // include or exclude entry (or the user must unselect the property).
            const exclude = Array.isArray(source.target_device_language_exclude)
                ? source.target_device_language_exclude
                : [];
            if (value.length === 0 && exclude.length === 0) {
                return 'User Language is required if selected. Either unselect it on the left list, or include or exclude at least one item.';
            }

            if (value.length === 0) {
                return;
            }

            return targetDeviceLanguageSchema;
        },

        target_device_language_exclude(value) {
            if (value === undefined || value === null) {
                return;
            }

            if (!_.isArray(value)) {
                return 'User Language exclude must be a list';
            }

            if (value.length === 0) {
                return;
            }

            return targetDeviceLanguageSchema;
        },

        geoboxes(value) {
            if (value === undefined) {
                return;
            }

            if (!_.isArray(value)) {
                return 'Geoboxes must be a list';
            }

            if (_.isArray(value) && value.length === 0) {
                return 'Geofencing is required if selected. Either unselect it on the left list, or enter at least one geofence.';
            }

            return geoboxSchema;
        },

        target_device_os(value) {
            if (value === undefined) {
                return;
            }

            if (!_.isArray(value)) {
                return 'Target Device OS must be a list';
            }

            return deviceOsSchema;
        },

        ad_preset_properties(value) {
            if (!isArray(1).validate(value)) {
                return 'Invalid ad preset properties';
            }

            const anyWidgetSelected = _.some(value, val => {
                return _.some(val.properties, prop => {
                    return prop.checked;
                });
            });

            if (!anyWidgetSelected) {
                return 'Select at least one preset option';
            }
        },
        target_dooh_publishers(value, source) {
            if (value === undefined) {
                return;
            }

            const include = value;
            const exclude = Array.isArray(source.target_dooh_publishers_exclude)
                ? source.target_dooh_publishers_exclude
                : [];

            // Compound include/exclude: when selected on the left list, require at least one
            // include or exclude entry (or the user must unselect the property).
            const includeEmpty = !Array.isArray(include) || include.length === 0;
            if (includeEmpty && exclude.length === 0) {
                return 'DOOH Publishers is required if selected. Either unselect it on the left list, or include or exclude at least one item.';
            }

            if (include && exclude) {
                const intersection = _.intersection(include, exclude);
                if (intersection.length > 0) {
                    return 'A DOOH publisher cannot be selected in both Include and Exclude fields';
                }
            }
        },
    };
}

function ageGroupSchema(value) {
    if (!isOneOf([-1, 1, 2, 3, 4, 5, 6, 7, 8])(value)) {
        return 'Age must be one of: -1, 1, 2, 3, 4, 5, 6, 7, 8';
    }
}

function gendersSchema(value) {
    if (!isOneOf(['M', 'F', 'D'])(value)) {
        return 'Gender must be one of: M, F, D';
    }
}

function deviceOsSchema(value) {
    // Lazy-load to avoid circular import via ads/business-logic → can → store in unit tests.
    const { DeviceOSMapping } = require('states/resources/ads/business-logic');
    const DEVICE_OSes = Object.values(DeviceOSMapping);
    if (!isOneOf(DEVICE_OSes)(value)) {
        return `Device OS must be one of: ${DEVICE_OSes.join(', ')}`;
    }
}

function targetDeviceLanguageSchema(value) {
    if (!isString(1).validate(value)) {
        return 'Target Device Language is required';
    }
}

//-- Custom validation helper --//
function isOneOf(list) {
    return function(value) {
        return _.includes(list, value);
    };
}
